import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';
import PlanRequestModeSelector, { PlanRequestMode } from './PlanRequestModeSelector';
import PlanRequestForm from './PlanRequestForm';
import CompletedPlanSelector from './CompletedPlanSelector';
import IncompleteSubmissionSelector from './IncompleteSubmissionSelector';
import PlanSubmissionSuccessModal from './PlanSubmissionSuccessModal';
import { WizardData } from '../../../context/WizardContext';
import { PlanRequest } from '../../../types/planRequest';
import { planRequestService } from '../../../services/planRequestService';
import { progressStorageService } from '../../../services/progressStorageService';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { useAdvisorConfigs } from '../../../hooks/useAdvisorConfig';
import { advisorDisplayLabel } from '../../../utils/advisorDisplay';
import { getClientNameFromFormData } from '../../../utils/clientNameFormatter';

interface NewPlanRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;

  // Reusability props
  mode?: PlanRequestMode;
  prefilledData?: Partial<WizardData>;
  prefilledSubmissionId?: string;
  skipModeSelection?: boolean;
}

type ModalStep = 'mode-selection' | 'plan-selection' | 'submission-selection' | 'form';

/**
 * New Plan Request Modal
 *
 * Main container for creating plan requests with 4 different modes:
 * 1. Start from Scratch - Empty form
 * 2. Revise Existing Plan - Pre-filled from completed plan
 * 3. Finish Incomplete - Pre-filled from incomplete submission
 * 4. Start with PDF - Coming soon (disabled)
 *
 * Reusable across the application (e.g., resume button in External Webform)
 */
const NewPlanRequestModal: React.FC<NewPlanRequestModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode: initialMode,
  prefilledData,
  prefilledSubmissionId,
  skipModeSelection = false
}) => {
  const { customerIds } = useUserAccess();
  // Accessible advisor configs — label the advisor selector with the SIPS id +
  // advisor name instead of the raw internal customerId.
  const { configs: accessibleConfigs } = useAdvisorConfigs();
  const advisorLabel = (customerId: string): string => advisorDisplayLabel(accessibleConfigs, customerId);
  const { token } = useAuth0Token();

  const [currentStep, setCurrentStep] = useState<ModalStep>('mode-selection');
  const [selectedMode, setSelectedMode] = useState<PlanRequestMode | null>(initialMode || null);
  const [selectedPlan, setSelectedPlan] = useState<PlanRequest | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<any | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [formData, setFormData] = useState<Partial<WizardData> | undefined>(prefilledData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submittedCaseId, setSubmittedCaseId] = useState<number | string | null>(null);
  const [submittedClientName, setSubmittedClientName] = useState('');
  const [submittedClientId, setSubmittedClientId] = useState('');

  // Initialize customer ID with first available
  useEffect(() => {
    if (customerIds.length > 0 && !selectedCustomerId) {
      setSelectedCustomerId(customerIds[0]);
    }
  }, [customerIds, selectedCustomerId]);

  // Handle initial mode and skip mode selection if specified
  useEffect(() => {
    if (open) {
      if (skipModeSelection && initialMode) {
        setSelectedMode(initialMode);
        setCurrentStep('form');
      } else {
        setCurrentStep('mode-selection');
        setSelectedMode(null);
      }
      setFormData(prefilledData);
      setError(null);
      setSuccess(false);
      // Set customer ID from prefilled data if available
      if (prefilledData?.customerId) {
        setSelectedCustomerId(prefilledData.customerId.toString());
      }
    }
  }, [open, initialMode, skipModeSelection, prefilledData]);

  const handleModeSelect = (mode: PlanRequestMode) => {
    setSelectedMode(mode);
    setError(null);

    switch (mode) {
      case 'scratch':
        setCurrentStep('form');
        setFormData(undefined);
        break;
      case 'revise':
        setCurrentStep('plan-selection');
        break;
      case 'finish-incomplete':
        setCurrentStep('submission-selection');
        break;
      case 'pdf-upload':
        // Not implemented yet
        break;
    }
  };

  const handlePlanSelect = (plan: PlanRequest | null) => {
    setSelectedPlan(plan);
    if (plan) {
      // Extract WizardData from the plan's snapshotData
      setFormData(plan.snapshotData || undefined);
      setCurrentStep('form');
    }
  };

  const handleSubmissionSelect = (submission: any | null) => {
    setSelectedSubmission(submission);
    if (submission) {
      setFormData(submission.formData);
      setCurrentStep('form');
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'plan-selection':
      case 'submission-selection':
      case 'form':
        if (skipModeSelection) {
          onClose();
        } else {
          setCurrentStep('mode-selection');
          setSelectedMode(null);
          setSelectedPlan(null);
          setSelectedSubmission(null);
          setFormData(undefined);
        }
        break;
      default:
        onClose();
    }
  };

  const handleFormSubmit = async (wizardData: WizardData, specialInstructions: string) => {
    setSubmitting(true);
    setError(null);

    try {
      // Use selected customer ID
      const customerId = selectedCustomerId ||
                         selectedSubmission?.customerId ||
                         selectedPlan?.customerId ||
                         wizardData.customerId ||
                         (customerIds.length > 0 ? customerIds[0] : '');

      if (!customerId) {
        throw new Error('Unable to determine customer ID');
      }

      // Get client name
      const clientName = getClientNameFromFormData(wizardData);

      // Build submission ID
      const submissionId = prefilledSubmissionId ||
                          selectedSubmission?.sessionId ||
                          `internal-${Date.now()}`;

      // Create plan request with full form data
      const planRequest = await planRequestService.createRequest({
        submissionId,
        customerId,
        clientName,
        isUpdate: selectedMode === 'revise',
        planTitle: selectedMode === 'revise' ? `Revision of ${selectedPlan?.planTitle || 'Existing Plan'}` : undefined,
        specialInstructions,
        formData: wizardData,
        isRevision: selectedMode === 'revise',
        originalPlanId: selectedPlan?.id
      }, token || undefined);

      // Call automation API through backend (hides API key, handles transformation)
      const apiBaseUrl = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8081';

      let caseId: number | string | null = null;
      let automationFailed = false;
      let automationErrorMessage = '';

      try {
        const automationResponse = await axios.post(
          `${apiBaseUrl}/api/automation/create-case`,
          {
            // Send the raw portal customerId — generated advisors use string
            // ids ("adv-xxxx"); parseInt would turn them into NaN → null.
            customerId,
            formData: wizardData,
            planRequestId: planRequest.id // Link automation to this plan request
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : undefined
            }
          }
        );
        console.log('Automation API called successfully for plan request:', planRequest.id);

        // Capture caseId from response
        caseId = automationResponse.data?.caseId || null;
      } catch (automationErr: any) {
        console.error('Error calling automation API:', automationErr);
        automationFailed = true;

        // Extract error message
        if (automationErr.response?.data?.message) {
          automationErrorMessage = automationErr.response.data.message;
        } else if (automationErr.response?.data?.error) {
          automationErrorMessage = automationErr.response.data.error;
        } else if (automationErr.message) {
          automationErrorMessage = automationErr.message;
        } else {
          automationErrorMessage = 'The automation service may be offline or unreachable.';
        }

        // Show warning but don't fail the whole operation
        console.warn('Plan request saved but automation failed:', automationErrorMessage);
      }

      // Mark the incomplete submission as complete if we have a sessionId
      if (prefilledSubmissionId) {
        try {
          await progressStorageService.saveProgress(
            wizardData,
            8, // Final step
            'Completed',
            prefilledSubmissionId,
            true, // isComplete = true
            token || undefined
          );
          console.log('Marked submission as complete:', prefilledSubmissionId);
        } catch (progressErr) {
          console.error('Failed to update progress status:', progressErr);
          // Don't fail the whole operation if this fails
        }
      }

      // If automation failed, show error instead of success
      if (automationFailed) {
        setError(
          `Plan request saved successfully, but automation failed: ${automationErrorMessage}\n\n` +
          `The plan request has been created (ID: ${planRequest.id}) but was not submitted to the automation engine. ` +
          `You can manually process this plan or contact support.`
        );
        return;
      }

      setSuccess(true);

      // Store data for success modal
      setSubmittedCaseId(caseId);
      setSubmittedClientName(clientName);
      setSubmittedClientId(planRequest.clientId);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Open success modal (don't close form modal yet - success modal will handle that)
      setSuccessModalOpen(true);
    } catch (err: any) {
      console.error('Error creating plan request:', err);
      setError(err.message || 'Failed to create plan request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 'mode-selection':
        return 'Add Client';
      case 'plan-selection':
        return 'Revise Existing Plan';
      case 'submission-selection':
        return 'Finish Incomplete Submission';
      case 'form':
        if (selectedMode === 'scratch') return 'Create New Plan Request';
        if (selectedMode === 'revise') return 'Revise Plan Request';
        if (selectedMode === 'finish-incomplete') return 'Complete Plan Request';
        return 'Plan Request Form';
      default:
        return 'Add Client';
    }
  };

  const canGoBack = currentStep !== 'mode-selection' || skipModeSelection;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {canGoBack && (
          <IconButton onClick={handleBack} edge="start">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1 }}>
          {getStepTitle()}
        </Box>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* The form step sits on the workspace grey so its section cards read
          like the Client Details tab; the picker steps keep the plain sheet. */}
      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', ...(currentStep === 'form' ? { bgcolor: '#F6F7F9' } : {}) }}
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Plan request created successfully!
          </Alert>
        )}

        {/* Mode Selection */}
        {currentStep === 'mode-selection' && (
          <PlanRequestModeSelector onSelectMode={handleModeSelect} />
        )}

        {/* Plan Selection (Mode 2) */}
        {currentStep === 'plan-selection' && (
          <CompletedPlanSelector
            onSelectPlan={handlePlanSelect}
            selectedPlan={selectedPlan}
          />
        )}

        {/* Submission Selection (Mode 3) */}
        {currentStep === 'submission-selection' && (
          <IncompleteSubmissionSelector
            onSelectSubmission={handleSubmissionSelect}
            selectedSubmission={selectedSubmission}
          />
        )}

        {/* Form */}
        {currentStep === 'form' && (
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {/* Customer ID Selector */}
            {customerIds.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="customer-id-select-label">Advisor</InputLabel>
                  <Select
                    labelId="customer-id-select-label"
                    id="customer-id-select"
                    value={selectedCustomerId}
                    label="Advisor"
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                  >
                    {customerIds.map((id) => (
                      <MenuItem key={id} value={id}>
                        {advisorLabel(id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Select which advisor this plan request is for
                </Typography>
              </Box>
            )}

            <PlanRequestForm
              initialData={formData}
              customerId={selectedCustomerId}
              onSubmit={handleFormSubmit}
              onCancel={handleBack}
              submitting={submitting}
            />
          </Box>
        )}

        {/* Next Button for Selection Screens */}
        {(currentStep === 'plan-selection' || currentStep === 'submission-selection') && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setCurrentStep('form')}
              disabled={
                (currentStep === 'plan-selection' && !selectedPlan) ||
                (currentStep === 'submission-selection' && !selectedSubmission)
              }
            >
              Next
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>

    {/* Success Modal */}
    <PlanSubmissionSuccessModal
      open={successModalOpen}
      onClose={() => {
        setSuccessModalOpen(false);
        onClose(); // Close the parent form modal when success modal closes
      }}
      caseId={submittedCaseId}
      clientName={submittedClientName}
      clientId={submittedClientId}
    />
  </>
  );
};

export default NewPlanRequestModal;
